<script lang="ts">
  import { getContext } from 'svelte';
  import * as Popover from '$lib/components/ui/popover';
  import * as Dialog from '$lib/components/ui/dialog';
  import AvailabilityPopover from '@/SetupWizards/AvailabilityPopover.svelte';
  import AvailabilityDialog from '@/SetupWizards/AvailabilityDialog.svelte';
  import { isSystemChosen, normalizeAvailability, sameAvailability, usualIntensity } from '@/SetupWizards/availability';
  import type { Availability } from '@/Common/constants';
  import type { MedianAndWeatherStores, MyFitnessStores } from '../stores';
  import { getRequest, getUsualAvailability, uniqueId } from '@/Util/utils';
  import { LocalDate } from '@/time';

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // The availability popover for a day of the calendar, with the button that opens it passed in as the content:
  //   <DayAvailabilityPopover {date} let:builder><Button builders={[builder]}>Availability</Button></DayAvailabilityPopover>
  // It keeps a copy of the day's settings to edit, made again each time it opens, and Save writes it to the day's own availability
  // (or, with showScope, to the weekday's when that is chosen), updating the calendar settings at once and telling the server.
  export let date: Date;
  // The planner's: a choice between this day and the weekday, and Reset.
  export let showScope = false;
  // Opened as a modal dialog, as the planner does, rather than a popover beside its button
  export let asModal = false;
  export let open = false;
  // Told after a save, with what the server answered (the training advice and targets for the new availability)
  export let onSaved: (data?: any) => void = () => {};

  const { cal_settings, trainingAdvice } = getContext<MyFitnessStores>('myFitness');
  const { trainingMedians } = getContext<MedianAndWeatherStores>('medianAndWeather');

  const id = uniqueId('dayAvailability-');
  // Beside the trigger only where there is room for it (the planner, on a wide screen); below it everywhere else
  let windowWidth = 1024;
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const weekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // The key the server and the settings know the day by: its local date, as the calendar reads it (not the UTC date of a
  // time of day, which east of UTC+12 is the day before)
  const getDateKey = () => LocalDate.format(date);
  const getWeekday = () => weekdays[date.getDay()];

  const weekdaySetting = (): Availability => normalizeAvailability($cal_settings.defaults[getWeekday()]);
  const daySetting = (): Availability => normalizeAvailability($cal_settings.availabilities?.[getDateKey()] ?? $cal_settings.defaults[getWeekday()]);

  let scope: 'day' | 'weekday' = 'day';
  let work: Availability = daySetting();
  const load = () => { work = scope === 'day' ? daySetting() : weekdaySetting(); };

  function onOpenChange(isOpen: boolean) {
    open = isOpen;
    if (isOpen) { scope = 'day'; resetPending = false; load(); }
  }

  // Reset sends the day back to its weekday's setting. Until something is edited after it, Save removes the day's own setting,
  // whatever the editor fills in for it as it saves (the usual time or duration of a field the system chooses).
  let resetPending = false;
  let resetWork = '';
  function reset() {
    work = weekdaySetting();
    resetWork = JSON.stringify(work);
    resetPending = true;
  }
  // An edit after Reset ends it. (Save's own fill-in happens just before it is handled, ahead of this.)
  $: if (resetPending && JSON.stringify(work) !== resetWork) resetPending = false;

  $: usual = getUsualAvailability($trainingMedians, date.getDay());

  // Red: this day is not as the weekday's setting is. Yellow: the system doesn't choose everything.
  $: resetDot = !showScope ? null
    : scope === 'day' && !sameAvailability(work, weekdaySetting(), usualIntensity(usual)) ? 'red' as const
    : !isSystemChosen(work, usualIntensity(usual)) ? 'yellow' as const : null;

  // Nothing to save while Save would change nothing: the weekdays as they are stored; a day as its own setting is stored, or, with
  // none, as its weekday's. A day the system chooses everything for is saved with the usual intensity (High on history-high
  // weekdays), so one stored as Either still has that to save.
  const settled = (a: Availability, usualI: ReturnType<typeof usualIntensity>) => a.availability && isSystemChosen(a, usualI) ? { ...a, intensity: usualI } : a;
  // Whether the day, as it would be saved, is its weekday's setting as stored (or what Reset gave it): it then needs no
  // setting of its own. As stored, not as shown: on a history-high weekday still stored as Either, a day the system chooses
  // everything for is saved as High, which its weekday is not, so it keeps a setting of its own and is planned as High.
  // The venue and training category count too: a day that differs from its weekday only in those keeps its own setting
  const sameVenue = () => {
    const own = $cal_settings.availabilities?.[getDateKey()], weekday = $cal_settings.defaults[getWeekday()];
    return !own || ((own.venue ?? null) === (weekday?.venue ?? null) && (own.training_category ?? null) === (weekday?.training_category ?? null));
  };
  const followsWeekday = (a: Availability) => resetPending
    || (sameVenue() && sameAvailability(normalizeAvailability(settled(a, usualIntensity(usual))), weekdaySetting()));
  $: unchanged = ($cal_settings, scope, work, resetPending, (() => {
    const usualI = usualIntensity(usual);
    const own = $cal_settings.availabilities?.[getDateKey()];
    // Without the usual intensity, sameAvailability compares as stored
    if (scope === 'weekday') return sameAvailability(settled(work, usualI), weekdaySetting());
    if (followsWeekday(work)) return !own;
    return !!own && sameAvailability(settled(work, usualI), normalizeAvailability(own));
  })());

  async function save(saved: Availability) {
    const a = normalizeAvailability(saved);
    const stored = scope === 'weekday' ? $cal_settings.defaults[getWeekday()] : ($cal_settings.availabilities?.[getDateKey()] ?? $cal_settings.defaults[getWeekday()]);
    const fields = {
      // The endpoint sets these too: sent as they are so they are kept
      venue: stored?.venue ?? null,
      training_category: stored?.training_category ?? null,
      availability: a.availability,
      time: a.time !== 'advisor' ? `${a.time}:00` : a.time,
      duration: a.duration,
      fixed_availability: a.fixed_availability,
      intensity: a.intensity,
    };
    const settings = $cal_settings;
    const dateKey = getDateKey(), weekday = getWeekday();
    // The one entry this changes, to put back if the server doesn't take it (only that entry: another day saved meanwhile stays)
    const copy = (v: any) => v === undefined ? undefined : JSON.parse(JSON.stringify(v));
    const target = scope === 'weekday' ? { kind: 'defaults', key: weekday } : { kind: 'availabilities', key: dateKey };
    const previous = copy(settings[target.kind]?.[target.key]);
    const own = settings.availabilities?.[dateKey];
    let request: Promise<Response> | null = null;
    if (scope === 'weekday') {
      settings.defaults[weekday] = { ...settings.defaults[weekday], ...fields };
      request = post('/calendar/training-availability', { date: dateKey, saveAsDefault: true, ...body(fields) });
    } else if (followsWeekday(a)) {
      // As the weekday's setting is: the day has no setting of its own
      if (own) {
        delete settings.availabilities[dateKey];
        request = post('/calendar/remove-training-availability', { date: dateKey });
      }
    } else {
      settings.availabilities = { ...(settings.availabilities ?? {}), [dateKey]: fields };
      request = post('/calendar/training-availability', { date: dateKey, saveAsDefault: false, ...body(fields) });
    }
    // The settings are told at once, which lights Adapt Forecast, and the server after
    cal_settings.set(settings);
    const written = JSON.stringify(settings[target.kind]?.[target.key]);
    if (!request) { onSaved(); return; }
    try {
      const response = await request;
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      // Today's availability answers with the new advice, other days with nothing
      const text = await response.text();
      const data = text ? JSON.parse(text) : null;
      if (data?.advice) trainingAdvice.set(data.advice);
      onSaved(data);
    } catch (e) {
      console.error(e);
      cal_settings.update(s => {
        // Unless it has been changed again since (a later save of the same day): that one stands
        if (JSON.stringify(s[target.kind]?.[target.key]) !== written) return s;
        const entries = { ...(s[target.kind] ?? {}) };
        if (previous === undefined) delete entries[target.key]; else entries[target.key] = previous;
        return { ...s, [target.kind]: entries };
      });
      // @ts-ignore
      window.Xert?.warning?.('Your availability could not be saved. Please try again.');
    }
  }
  // The endpoint takes the venue and training category as one pair
  const body = ({ venue, training_category, ...rest }: any) => ({ ...rest, venue: [venue, training_category] });
  const post = (url: string, body: any) => fetch(url, { ...getRequest(), method: 'POST', body: JSON.stringify(body) });
</script>

<svelte:window bind:innerWidth={windowWidth} />

<!-- Into the page's dialog target, where the components are styled on every page -->
{#if asModal}
  <Dialog.Root portal="#shadcn-ui-dialog-target" {open} {onOpenChange}>
    <Dialog.Trigger asChild let:builder>
      <slot {builder} />
    </Dialog.Trigger>
    <AvailabilityDialog bind:defaultAvailability={work} {id} dowLabel={weekdayNames[date.getDay()]}
      dateLabel={date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
      medians={usual}
      showSave saveDisabled={unchanged} scopeLabel={showScope ? `${weekdayNames[date.getDay()]}s` : null} bind:scope {resetDot}
      on:scope={(e) => { scope = e.detail; load(); }} on:reset={reset} on:save={(e) => save(e.detail)} />
  </Dialog.Root>
{:else}
  <Popover.Root portal="#shadcn-ui-dialog-target" {open} {onOpenChange}>
    <Popover.Trigger asChild let:builder>
      <slot {builder} />
    </Popover.Trigger>
    <AvailabilityPopover bind:defaultAvailability={work} {id} dowLabel={weekdayNames[date.getDay()]}
      medians={usual}
      showSave side={showScope && windowWidth >= 768 ? 'right' : 'bottom'} saveDisabled={unchanged} scopeLabel={showScope ? `${weekdayNames[date.getDay()]}s` : null} bind:scope {resetDot}
      on:scope={(e) => { scope = e.detail; load(); }} on:reset={reset} on:save={(e) => save(e.detail)} />
  </Popover.Root>
{/if}
